// src/components/TwoFactorVerify.jsx
// Two-Factor Authentication Verification Component for Login Flow

import React, { useState, useRef, useEffect } from 'react';
import {
  Shield,
  Key,
  AlertTriangle,
  ArrowLeft,
  Smartphone,
  RefreshCw
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TwoFactorVerify = ({ tempToken, email, onSuccess, onCancel, onBackupUsed }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);

  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Handle individual digit input
  const handleDigitChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        verifyCode(fullCode);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pastedData[i] || '';
      }
      setCode(newCode);

      // Focus appropriate input
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if complete
      if (pastedData.length === 6) {
        verifyCode(pastedData);
      }
    }
  };

  // Verify TOTP code
  const verifyCode = async (codeToVerify) => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        credentials: 'include',
        body: JSON.stringify({ code: codeToVerify })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        if (data.attemptsRemaining !== undefined) {
          setAttemptsRemaining(data.attemptsRemaining);
        }
        throw new Error(data.error || 'Verification failed');
      }

      // Success - call onSuccess with the new token and user data
      onSuccess({
        token: data.token,
        user: data.user
      });

    } catch (err) {
      setError(err.message);
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Verify backup code
  const verifyBackupCode = async () => {
    const cleanCode = backupCode.replace(/[^A-Za-z0-9-]/g, '').toUpperCase();

    if (cleanCode.length < 8) {
      setError('Please enter a valid backup code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/verify-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        credentials: 'include',
        body: JSON.stringify({ backupCode: cleanCode })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Invalid backup code');
      }

      // Notify that backup code was used
      if (onBackupUsed) {
        onBackupUsed(data.remainingCodes);
      }

      // Success
      onSuccess({
        token: data.token,
        user: data.user
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset to TOTP entry
  const switchToTotp = () => {
    setUseBackupCode(false);
    setBackupCode('');
    setError('');
    setCode(['', '', '', '', '', '']);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#172A3A] to-[#0D1B2A] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="text-blue-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#172A3A]">
            Two-Factor Authentication
          </h1>
          <p className="text-gray-600 mt-2">
            {email && <span className="block text-sm">Signing in as {email}</span>}
            {useBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the code from your authenticator app'
            }
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              <span>{error}</span>
              {attemptsRemaining < 5 && (
                <span className="block text-sm mt-1">
                  {attemptsRemaining} attempts remaining
                </span>
              )}
            </div>
          </div>
        )}

        {/* TOTP Code Entry */}
        {!useBackupCode && (
          <div>
            <div className="flex justify-center gap-2 mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={loading}
                  className="w-12 h-14 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg
                           focus:border-[#FFB800] focus:ring-2 focus:ring-[#FFB800]/20 outline-none
                           disabled:bg-gray-100 disabled:cursor-not-allowed transition"
                />
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Smartphone size={16} />
              <span>Code refreshes every 30 seconds</span>
            </div>

            <button
              onClick={() => verifyCode(code.join(''))}
              disabled={loading || code.join('').length !== 6}
              className="w-full bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg
                       transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
          </div>
        )}

        {/* Backup Code Entry */}
        {useBackupCode && (
          <div>
            <div className="mb-6">
              <input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                disabled={loading}
                className="w-full text-center text-xl font-mono py-4 border-2 border-gray-300 rounded-lg
                         focus:border-[#FFB800] focus:ring-2 focus:ring-[#FFB800]/20 outline-none
                         disabled:bg-gray-100 uppercase tracking-widest"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Each backup code can only be used once
              </p>
            </div>

            <button
              onClick={verifyBackupCode}
              disabled={loading || backupCode.length < 8}
              className="w-full bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 px-4 rounded-lg
                       transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                'Use Backup Code'
              )}
            </button>
          </div>
        )}

        {/* Toggle between TOTP and Backup Code */}
        <div className="mt-6 text-center">
          {useBackupCode ? (
            <button
              onClick={switchToTotp}
              className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mx-auto"
            >
              <Smartphone size={14} />
              Use authenticator app instead
            </button>
          ) : (
            <button
              onClick={() => {
                setUseBackupCode(true);
                setError('');
              }}
              className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mx-auto"
            >
              <Key size={14} />
              Use a backup code
            </button>
          )}
        </div>

        {/* Cancel / Back to Login */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="mt-6 w-full py-2 text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2 transition"
          >
            <ArrowLeft size={16} />
            Back to login
          </button>
        )}

        {/* Help Text */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 text-center">
            Having trouble? Contact support or use a backup code if you've lost access to your authenticator app.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerify;
